import React, { useEffect } from 'react'
import { Modal, Spinner } from 'reactstrap'
import { FiX, FiPlus } from 'react-icons/fi'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import { editProductAction } from '_redux/modules/product'
import { SELECTION_MODE_MULTIPLE, SELECTION_MODE_SINGLE } from '_utils/constant'
import useHasOrder from '_hooks/useHasOrder'
import ButtonCommon from '../../../components/Button'
import './style.scss'

const subItemSchema = Yup.object().shape({
  subItems: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().trim().required('Title is required'),
      selectionMode: Yup.string()
        .oneOf([SELECTION_MODE_SINGLE, SELECTION_MODE_MULTIPLE])
        .default(SELECTION_MODE_SINGLE),
      trackInventory: Yup.boolean().default(false),
      required: Yup.boolean().when('trackInventory', {
        is: true,
        then: Yup.boolean().oneOf(
          [true],
          'Required is required when Choose Quantities is selected'
        ),
      }),
      list: Yup.array()
        .min(1, 'At least 1 option is required')
        .of(
          Yup.object().shape({
            name: Yup.string().trim().required('Name is required'),
            price: Yup.number()
              .typeError('Price is required')
              .required('Price is required')
              .min(0, 'Price must be >= 0'),
            maxQty: Yup.mixed().test('maxQty-validate', function validateMaxQty(val) {
              const group = this.from?.[1]?.value
              if (group?.trackInventory) {
                if (val === '' || val === null || val === undefined) {
                  return this.createError({ message: 'Max Quantity is required' })
                }
                const num = Number(val)
                if (isNaN(num)) {
                  return this.createError({ message: 'Max Quantity must be a number' })
                }
                if (num < 0) {
                  return this.createError({ message: 'Max Quantity must be >= 0' })
                }
              }
              return true
            }),
          })
        ),
    })
  ),
})

const getDefaultSubItems = () => [
  {
    type: '',
    selectionMode: SELECTION_MODE_SINGLE,
    trackInventory: false,
    required: false,
    list: [{ name: '', price: '', maxQty: '' }],
  },
]

export default function ModalSubItems({ isOpen, toggle, row, hasOrder: propHasOrder }) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const hasOrderFromHook = useHasOrder(row?.eventId)
  const hasOrder = propHasOrder !== undefined ? propHasOrder : hasOrderFromHook
  const hasExistingSubItems = Boolean(row?.subItems && row.subItems.length > 0)
  const isSelectionModeDisabled = Boolean(hasExistingSubItems && hasOrder)

  const {
    control,
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(subItemSchema),
    defaultValues: {
      subItems: getDefaultSubItems(),
    },
  })

  const {
    fields: subItemFields,
    append: appendSubItem,
    remove: removeSubItem,
  } = useFieldArray({
    control,
    name: 'subItems',
  })

  useEffect(() => {
    if (isOpen) {
      const subItems = row?.subItems?.length
        ? row.subItems.map((item) => ({
            type: item.type || '',
            selectionMode: item.selectionMode || SELECTION_MODE_SINGLE,
            trackInventory: Boolean(item.trackInventory),
            required: Boolean(item.required),
            list: item.list?.length
              ? item.list.map((opt) => ({
                  name: opt.name || '',
                  price: opt.price ?? '',
                  maxQty: opt.maxQty ?? '',
                }))
              : [{ name: '', price: '', maxQty: '' }],
          }))
        : getDefaultSubItems()

      reset({ subItems })
    } else {
      reset({ subItems: getDefaultSubItems() })
    }
  }, [isOpen, row?.id, reset])

  const handleClose = () => {
    reset({ subItems: getDefaultSubItems() })
    toggle()
  }

  const handleSelectionModeChange = (groupIndex, modeStr) => {
    if (isSelectionModeDisabled) return
    const isQuantities = modeStr === 'quantities'
    const mode = isQuantities ? SELECTION_MODE_SINGLE : modeStr
    setValue(`subItems.${groupIndex}.selectionMode`, mode, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue(`subItems.${groupIndex}.trackInventory`, isQuantities, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const onSubmit = async (data) => {
    if (!row) {
      handleClose()
      return
    }

    const formattedSubItems = (data.subItems || []).map(({ id: _id, ...subItem }) => {
      const trackInventory = Boolean(subItem.trackInventory)
      const selectionMode = trackInventory
        ? SELECTION_MODE_SINGLE
        : subItem.selectionMode || SELECTION_MODE_SINGLE

      return {
        ...subItem,
        selectionMode,
        trackInventory,
        required: subItem.required ? 1 : 0,
        list: (subItem.list || []).map((option) => ({
          name: option.name.trim(),
          price: Number(option.price),
          ...(trackInventory && option.maxQty !== '' && option.maxQty !== undefined
            ? { maxQty: Number(option.maxQty) }
            : {}),
        })),
      }
    })

    try {
      const { msgResp } = await dispatch(
        editProductAction(row.id, row.eventId, {
          subItems: formattedSubItems,
        })
      )
      addToast(msgResp || 'Sub-items updated successfully', {
        appearance: 'success',
        autoDismiss: true,
      })
      handleClose()
    } catch (error) {
      const errorMsg = error?.msgResp || 'Update failed'
      addToast(errorMsg, { appearance: 'error', autoDismiss: true })
    }
  }

  return (
    <Modal isOpen={isOpen} centered toggle={handleClose} className="modal-sub-items">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="prod-modal-hd">
          <span className="prod-modal-title">Add Sub-items</span>
          <button type="button" className="prod-modal-close" onClick={handleClose}>
            <FiX size={14} />
          </button>
        </div>

        <div className="prod-modal-body">
          {subItemFields.map((group, index) => {
            const groupError = errors?.subItems?.[index]
            const currentGroup = watch(`subItems.${index}`) || {}
            const selectionMode = currentGroup.selectionMode || SELECTION_MODE_SINGLE
            const trackInventory = Boolean(currentGroup.trackInventory)
            const isChooseQuantity = Boolean(trackInventory || selectionMode === 'quantities')
            const isDeleteDisabled = Boolean(hasOrder && hasExistingSubItems)
            const isRequiredDisabled = Boolean(isChooseQuantity && hasOrder)

            return (
              <div key={group.id} className="sub-group">
                <div className="sub-title-row">
                  <input
                    type="text"
                    placeholder="Sub-item title (e.g. Frame Color)"
                    className={`sub-input-title ${groupError?.type ? 'is-invalid' : ''}`}
                    {...register(`subItems.${index}.type`)}
                  />
                  <button
                    type="button"
                    disabled={isDeleteDisabled}
                    className={`sub-del ${isDeleteDisabled ? 'disabled' : ''}`}
                    onClick={() => {
                      if (isDeleteDisabled) return
                      removeSubItem(index)
                    }}
                    title={
                      isDeleteDisabled
                        ? 'Cannot delete sub-item because this event has orders'
                        : 'Remove group'
                    }
                  >
                    <FiX size={12} />
                  </button>
                </div>
                {groupError?.type && (
                  <span className="text-danger" style={{ fontSize: '11px' }}>
                    {groupError.type.message}
                  </span>
                )}

                <div className="sub-choice-headers">
                  <span className="col-choice-name">Choice name</span>
                  <span className="col-addon-cost">Add-on cost</span>
                  {trackInventory && <span className="col-max-qty">Max Quantity</span>}
                </div>

                <SubItemChoices
                  control={control}
                  register={register}
                  groupIndex={index}
                  errors={errors}
                  trackInventory={trackInventory}
                />

                <div className="selection-mode-row">
                  <span>Selection Mode</span>
                  <div
                    className={`selection-toggle-pill ${isSelectionModeDisabled ? 'disabled' : ''}`}
                    data-selection-type={trackInventory ? 'quantities' : selectionMode}
                    title={isSelectionModeDisabled ? 'Selection mode cannot be changed once orders exist' : undefined}
                  >
                    <button
                      type="button"
                      disabled={isSelectionModeDisabled}
                      className={selectionMode === SELECTION_MODE_SINGLE && !trackInventory ? 'active' : ''}
                      onClick={() => handleSelectionModeChange(index, SELECTION_MODE_SINGLE)}
                    >
                      Choose One
                    </button>
                    <button
                      type="button"
                      disabled={isSelectionModeDisabled}
                      className={selectionMode === SELECTION_MODE_MULTIPLE && !trackInventory ? 'active' : ''}
                      onClick={() => handleSelectionModeChange(index, SELECTION_MODE_MULTIPLE)}
                    >
                      Build a Combo
                    </button>
                    <button
                      type="button"
                      disabled={isSelectionModeDisabled}
                      className={selectionMode === SELECTION_MODE_SINGLE && trackInventory ? 'active' : ''}
                      onClick={() => handleSelectionModeChange(index, 'quantities')}
                    >
                      Choose Quantities
                    </button>
                  </div>
                </div>

                <SubItemModePreview mode={trackInventory ? 'quantities' : selectionMode} />

                <div className="sub-required-row">
                  <span>Required</span>
                  <Controller
                    control={control}
                    name={`subItems.${index}.required`}
                    render={({ field: { value, onChange } }) => (
                      <div
                        className={`toggle-switch ${value ? 'active' : ''} ${
                          isRequiredDisabled ? 'disabled' : ''
                        }`}
                        onClick={() => {
                          if (isRequiredDisabled) return
                          onChange(!value)
                        }}
                        title={
                          isRequiredDisabled
                            ? 'Required cannot be changed when Choose Quantities is selected and event has orders'
                            : undefined
                        }
                      >
                        <div className="toggle-knob" />
                      </div>
                    )}
                  />
                </div>
                {index < subItemFields.length - 1 && (
                  <div style={{ borderTop: '0.5px solid #E0DFD8', marginTop: '14px' }} />
                )}
                {errors?.subItems?.[index]?.required && (
                  <span className="text-danger" style={{ fontSize: '11px' }}>
                    {errors?.subItems?.[index]?.required.message}
                  </span>
                )}
              </div>
            )
          })}
          <div style={{ paddingTop: subItemFields.length > 0 ? '4px' : '0', display: subItemFields.length > 0 ? 'none' : 'block' }}>
            <button
              type="button"
              className="sub-add-btn"
              onClick={() =>
                appendSubItem({
                  type: '',
                  selectionMode: SELECTION_MODE_SINGLE,
                  trackInventory: false,
                  required: false,
                  list: [{ name: '', price: '', maxQty: '' }],
                })
              }
            >
              <FiPlus size={12} />
              Add sub-item title
            </button>
          </div>
        </div>

        <div className="modal-sub-items-footer">
          <ButtonCommon
            variant="outline"
            content="Cancel"
            onClick={handleClose}
          />
          <ButtonCommon
            variant="primary"
            htmlType="submit"
            disabled={isSubmitting}
            icon={isSubmitting ? <Spinner size="sm" /> : null}
            content="Save"
          />
        </div>
      </form>
    </Modal>
  )
}

function SubItemChoices({ control, register, groupIndex, errors, trackInventory }) {
  const {
    fields: choiceFields,
    append: appendChoice,
    remove: removeChoice,
  } = useFieldArray({
    control,
    name: `subItems.${groupIndex}.list`,
  })

  return (
    <>
      <div className="sub-entry-list">
        {choiceFields.map((choice, choiceIndex) => {
          const choiceError = errors?.subItems?.[groupIndex]?.list?.[choiceIndex]
          return (
            <div key={choice.id} className="d-flex flex-column gap-1">
              <div className="sub-entry">
                <input
                  type="text"
                  placeholder="e.g. Blue"
                  className={`sub-input-name ${choiceError?.name ? 'is-invalid' : ''}`}
                  {...register(`subItems.${groupIndex}.list.${choiceIndex}.name`)}
                />
                <span className="cost-prefix">+$</span>
                <input
                  type="number"
                  step="any"
                  placeholder="0"
                  className={`sub-input-cost ${choiceError?.price ? 'is-invalid' : ''}`}
                  {...register(`subItems.${groupIndex}.list.${choiceIndex}.price`)}
                />
                {trackInventory && (
                  <input
                    type="number"
                    step="any"
                    placeholder="0"
                    className={`sub-input-maxqty ${choiceError?.maxQty ? 'is-invalid' : ''}`}
                    {...register(`subItems.${groupIndex}.list.${choiceIndex}.maxQty`)}
                  />
                )}
                <button
                  type="button"
                  className="sub-del"
                  onClick={() => removeChoice(choiceIndex)}
                  title="Remove choice"
                >
                  <FiX size={12} />
                </button>
              </div>
              {(choiceError?.name
                || choiceError?.price
                || (trackInventory && choiceError?.maxQty)) && (
                <div className="d-flex justify-content-between px-1" style={{ fontSize: '11px' }}>
                  <span className="text-danger">{choiceError?.name?.message || ''}</span>
                  <span className="text-danger me-4">{choiceError?.price?.message || ''}</span>
                  {trackInventory && (
                    <span className="text-danger me-4">{choiceError?.maxQty?.message || ''}</span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className="sub-add-btn"
        onClick={() => appendChoice({ name: '', price: '', maxQty: '' })}
      >
        <FiPlus size={12} />
        Add choices
      </button>
    </>
  )
}

function SubItemModePreview({ mode = SELECTION_MODE_SINGLE }) {
  if (mode === SELECTION_MODE_SINGLE) {
    return (
      <div className="sub-item-mode-preview">
        <div className="subprev-container">
          <div className="subprev-diagram">
            <div className="subprev-chk-row">
              <div className="subprev-radio" />
            </div>
            <div className="subprev-chk-row">
              <div className="subprev-radio on" />
            </div>
            <div className="subprev-ov">
              <div className="subprev-bc minus">−</div>
              <span className="subprev-bv">2</span>
              <div className="subprev-bc plus">+</div>
            </div>
          </div>
          <span className="subprev-text">
            Choose one option for this product, then set how many of this combination
            you want.
          </span>
        </div>
      </div>
    )
  }

  if (mode === SELECTION_MODE_MULTIPLE) {
    return (
      <div className="sub-item-mode-preview">
        <div className="subprev-container">
          <div className="subprev-diagram">
            <div className="subprev-chk-row">
              <div className="subprev-chk" />
              <span className="subprev-mv">2</span>
            </div>
            <div className="subprev-chk-row">
              <div className="subprev-chk" />
              <span className="subprev-mv">2</span>
            </div>
            <div className="subprev-ov">
              <div className="subprev-bc minus">−</div>
              <span className="subprev-bv">2</span>
              <div className="subprev-bc plus">+</div>
            </div>
          </div>
          <span className="subprev-text">
            Choose multiple options, then set how many of this combination you want.
          </span>
        </div>
      </div>
    )
  }

  if (mode === 'quantities') {
    return (
      <div className="sub-item-mode-preview">
        <div className="subprev-container">
          <div className="subprev-diagram">
            <div className="subprev-sp-row">
              <div className="subprev-sc minus">−</div>
              <span className="subprev-sv">10</span>
              <div className="subprev-sc plus">+</div>
            </div>
            <div className="subprev-sp-row">
              <div className="subprev-sc minus">−</div>
              <span className="subprev-sv">2</span>
              <div className="subprev-sc plus">+</div>
            </div>
            <div className="subprev-sp-row">
              <div className="subprev-sc minus">−</div>
              <span className="subprev-sv">5</span>
              <div className="subprev-sc plus">+</div>
            </div>
          </div>
          <span className="subprev-text">
            Set a separate quantity for each option.
          </span>
        </div>
      </div>
    )
  }

  return null
}
